import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../services/errorMessages';
import { FiUsers, FiUserPlus, FiActivity, FiFileText, FiLogOut, FiMenu, FiX, FiPaperclip, FiLock, FiHome, FiDatabase } from 'react-icons/fi';
import { useState } from 'react';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';
import './Sidebar.css';

const Sidebar = () => {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [showLogoutWarn, setShowLogoutWarn] = useState(false);

  const adminLinks = [
    { to: '/users', icon: <FiUserPlus />, label: 'თანამშრომლები' },
    { to: '/clients', icon: <FiUsers />, label: 'ბენეფიციარები' },
    { to: '/activities', icon: <FiActivity />, label: 'აქტივობები' },
    { to: '/attachments', icon: <FiPaperclip />, label: 'დანართები' },
    { to: '/logs', icon: <FiFileText />, label: 'ისტორია' },
    ...(isSuperAdmin ? [{ to: '/backups', icon: <FiDatabase />, label: 'ბექაფი' }] : []),
  ];

  const userLinks = [
    { to: '/dashboard', icon: <FiHome />, label: 'მთავარი' },
  ];

  const links = isAdmin ? adminLinks : userLinks;

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (pwForm.newPw !== pwForm.confirm) {
      setPwError('პაროლები არ ემთხვევა');
      return;
    }
    if (pwForm.newPw.length < 4) {
      setPwError('პაროლი მინიმუმ 4 სიმბოლო');
      return;
    }

    try {
      await api.put('/auth/change-password', {
        current_password: pwForm.current,
        new_password: pwForm.newPw,
      });
      setPwSuccess('პაროლი შეიცვალა, გადამისამართება...');
      setPwForm({ current: '', newPw: '', confirm: '' });
      setTimeout(() => { logout(); }, 1500);
    } catch (err) {
      setPwError(getErrorMessage(err));
    }
  };

  return (
    <>
      {!isOpen && (
        <button className="menu-toggle" onClick={() => setIsOpen(true)}>
          <FiMenu />
        </button>
      )}

      <div className={`sidebar-overlay ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(false)} />

      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <span className="logo-icon">A</span>
            <span className="logo-text">ASISTENTI</span>
          </div>
          <button className="sidebar-close" onClick={() => setIsOpen(false)}>
            <FiX />
          </button>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.first_name} {user?.last_name}</span>
            <span className="user-role">{isSuperAdmin ? 'OWNER' : isAdmin ? 'ადმინისტრატორი' : 'თანამშრომელი'}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={() => setIsOpen(false)}
            >
              <span className="nav-icon">{link.icon}</span>
              <span className="nav-label">{link.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <button className="pw-change-btn" onClick={() => { setShowPassword(true); setPwError(''); setPwSuccess(''); setPwForm({ current: '', newPw: '', confirm: '' }); }}>
            <FiLock />
            <span>პაროლის შეცვლა</span>
          </button>
          <button className="logout-btn" onClick={async () => {
            if (!isAdmin) {
              try {
                const { data } = await api.get('/work/active');
                if (data) { setShowLogoutWarn(true); return; }
              } catch { /* ignored */ }
            }
            logout();
          }}>
            <FiLogOut />
            <span>გასვლა</span>
          </button>
        </div>
      </aside>

      {showPassword && (
        <div className="pw-overlay" onClick={() => setShowPassword(false)}>
          <div className="pw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pw-modal-header">
              <h3>პაროლის შეცვლა</h3>
              <button className="pw-close" onClick={() => setShowPassword(false)}><FiX /></button>
            </div>
            <form onSubmit={handlePasswordChange} className="pw-form">
              {pwError && <div className="pw-error">{pwError}</div>}
              {pwSuccess && <div className="pw-success">{pwSuccess}</div>}
              <div className="pw-field">
                <label>მიმდინარე პაროლი</label>
                <input type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} required />
              </div>
              <div className="pw-field">
                <label>ახალი პაროლი</label>
                <input type="password" value={pwForm.newPw} onChange={(e) => setPwForm({ ...pwForm, newPw: e.target.value })} required />
              </div>
              <div className="pw-field">
                <label>გაიმეორეთ ახალი პაროლი</label>
                <input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} required />
              </div>
              <button type="submit" className="pw-submit" disabled={!pwForm.current || pwForm.newPw.length < 4 || pwForm.newPw !== pwForm.confirm}>შეცვლა</button>
            </form>
          </div>
        </div>
      )}

      {showLogoutWarn && (
        <ConfirmModal
          title="სამუშაო აქტიურია"
          message="სამუშაო დაწყებული გაქვთ და დანართი არ არის შევსებული. ნამდვილად გსურთ გასვლა? დრო ჩვეულებრივად გაგრძელდება."
          type="warning"
          confirmText="გასვლა"
          cancelText="დაბრუნება"
          onConfirm={logout}
          onCancel={() => setShowLogoutWarn(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
